import { useEffect, useEffectEvent } from "react";

// Long enough that ⌥+key combos press their second key before dictation starts.
export const HOLD_TO_DICTATE_DELAY_MS = 200;

type Options = {
  enabled: boolean;
  canStart: (e: KeyboardEvent) => boolean;
  onHold: () => void;
  onRelease: () => void;
};

// Browsers never see a bare Fn press on macOS, so right ⌥ stands in for it.
export function useHoldToDictate({
  enabled,
  canStart,
  onHold,
  onRelease,
}: Options) {
  const canStartEvent = useEffectEvent(canStart);
  const hold = useEffectEvent(onHold);
  const release = useEffectEvent(onRelease);

  useEffect(() => {
    if (!enabled) return;
    let holdTimer: number | null = null;
    let holding = false;

    const disarm = () => {
      if (holdTimer !== null) clearTimeout(holdTimer);
      holdTimer = null;
    };
    const stop = () => {
      disarm();
      if (!holding) return;
      holding = false;
      release();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== "AltRight" || e.shiftKey || e.metaKey || e.ctrlKey) {
        disarm();
        return;
      }
      if (
        e.repeat ||
        e.isComposing ||
        holdTimer !== null ||
        holding ||
        document.querySelector("dialog[open]") ||
        !canStartEvent(e)
      )
        return;
      holdTimer = window.setTimeout(() => {
        holdTimer = null;
        holding = true;
        hold();
      }, HOLD_TO_DICTATE_DELAY_MS);
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === "AltRight") stop();
    };

    document.addEventListener("keydown", handleKeyDown, { capture: true });
    document.addEventListener("keyup", handleKeyUp, { capture: true });
    window.addEventListener("blur", stop);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
      document.removeEventListener("keyup", handleKeyUp, { capture: true });
      window.removeEventListener("blur", stop);
      disarm();
    };
  }, [enabled]);
}

export function spliceDictation(before: string, spoken: string, after: string) {
  const text = spoken.trim();
  if (!text)
    return { value: before + after, start: before.length, end: before.length };
  const start =
    before && !/\s$/.test(before) ? before.length + 1 : before.length;
  const head = `${before.padEnd(start)}${text}`;
  const tail = after && !/^\s/.test(after) ? ` ${after}` : after;
  return { value: head + tail, start, end: head.length };
}
